<template>
  <div ref="statePlots" id="state-plots" class="transition-plots">
    <div class="state-plot-container">
      <h2 v-show="!showPatient" class="title">
        A. Patient Model with Molecular Classification
        <img class="loading" src="../assets/loader.gif" />
        <img class="placeholder" src="../assets/placeholder.png" />
        <div class="wait-warning">
          Hold still! It takes just a couple of seconds...
        </div>
      </h2>
      <div v-show="showPatient">
        <!-- This Patient Graph -->

        <div class="buttons">
          <sui-button-group class="right">
            <sui-button
              @click="showCIErrors = true"
              :class="[showCIErrors ? 'active' : 'inactive']"
              >Show 95% CI</sui-button
            >
            <sui-button
              @click="showCIErrors = false"
              :class="[!showCIErrors ? 'active' : 'inactive']"
              >Hide</sui-button
            >
          </sui-button-group>

          <sui-button-group class="right">
            <sui-button
              @click="toggleDiagnosisCR(true)"
              :class="[timeFromDiagnosis ? 'active' : 'inactive']"
              >from Diagnosis</sui-button
            >
            <sui-button
              @click="toggleDiagnosisCR(false)"
              :class="[!timeFromDiagnosis ? 'active' : 'inactive']"
              >from CR</sui-button
            >
          </sui-button-group>
        </div>

        <h2 class="title">
          A. Patient Model with
          <span :class="['ui label title-label']">{{ componentLabel }}</span>
          Molecular Classification
        </h2>
        <div id="patient-chart" class="center-flex"></div>
      </div>
    </div>

    <div class="state-plot-container">
      <h2 v-show="!showAverageCategory" class="title">
        B. Average Patient by Risk Score
        <img class="loading" src="../assets/loader.gif" />
        <img class="placeholder" src="../assets/placeholder.png" />
      </h2>
      <div v-show="showAverageCategory">
        <!-- Classification Graphs NEW/ELN -->
        <div class="buttons">
          <sui-button-group class="right">
            <sui-button
              @click="toggleNewELN(true)"
              :class="[newProposal ? 'active' : 'inactive']"
              >Tazi et al</sui-button
            >
            <sui-button
              @click="toggleNewELN(false)"
              :class="[!newProposal ? 'active' : 'inactive']"
              >ELN 2017</sui-button
            >
          </sui-button-group>
        </div>

        <h2 class="title">
          B. Average
          <span
            v-if="newProposal ? newCategory : elnCategory"
            :class="[
              'ui label title-label',
              newProposal ? newCategory : elnCategory
            ]"
            >{{
              newProposal
                ? newCategory.toUpperCase()
                : elnCategory.toUpperCase()
            }}</span
          >
          Patient in {{ newProposal ? "Proposal" : "ELN 2017" }}
          Risk Score
        </h2>
        <div id="average-chart" class="center-flex"></div>
      </div>
    </div>

    <div class="state-plot-container">
      <h2 v-show="!showAveragePatient" class="title">
        C. Average Patient by Molecular Classification
        <img class="loading" src="../assets/loader.gif" />
        <img class="placeholder" src="../assets/placeholder.png" />
      </h2>
      <div v-show="showAveragePatient">
        <!-- Main Component Graph -->
        <h2 class="title">
          C. Average Patient in
          <span v-if="componentLabel" :class="['ui label title-label']">
            {{ componentLabel }}
          </span>
          Molecular Classification
        </h2>
        <div id="component-chart" class="center-flex"></div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import transitionMixin from "@/mixins/transition";

const selectorsMap = {
  patient: "#patient-chart",
  average: "#average-chart",
  component: "#component-chart"
};

export default {
  name: "StatePlots",
  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      d3: this.$d3,
      selectorsMap: selectorsMap,
      selector: "#state-plots",
      timeFromDiagnosis: true,
      newProposal: true,
      elements: {
        patient: {},
        average: {},
        component: {}
      }
    };
  },
  mounted() {
    this.renderPlots();
  },
  updated() {
    Vue.nextTick(() => this.updatePlots());
  },
  computed: {
    ...mapGetters([
      "thisPatient",
      "thisPatientLoading",
      "newLoading",
      "newClassification",
      "elnClassification",
      "newCategory",
      "elnCategory",
      "newPrincipleComponent",
      "predictError"
    ]),
    showPatient() {
      return !this.thisPatientLoading && this.plotPatient && this.plotComponent;
    },
    showAverageCategory() {
      return !this.newLoading && this.plotNew && this.plotEln;
    },
    showAveragePatient() {
      return !this.newLoading && this.plotComponent;
    },
    plotPatient() {
      return Object.keys(this.thisPatient).length;
    },
    plotNew() {
      return Object.keys(this.newClassification).length;
    },
    plotEln() {
      return Object.keys(this.elnClassification).length;
    },
    plotComponent() {
      return Object.keys(this.newClassification).length;
    },
    componentLabel() {
      let value = this.newPrincipleComponent.replace(/_/g, " ");
      if (
        ["favorable", "intermediate", "adverse", "no events"].includes(value)
      ) {
        value = value.toUpperCase();
      }
      return value;
    },
    plots() {
      return this.avgPlots.concat(this.thisPatientPlots);
    },
    avgPlots() {
      const plots = [];
      const xlabel = this.timeFromDiagnosis ? "Diagnosis" : "CR";

      const classifier = this.newProposal
        ? this.newClassification
        : this.elnClassification;

      const averageData =
        xlabel === "Diagnosis"
          ? classifier["average_ptA_diagnosis"]
          : classifier["average_ptA_CR"];

      // Average plot
      if (averageData) {
        plots.push({
          data: averageData.filter((_, index) => index % 3 == 0),
          type: "average",
          xlabel
        });
      }

      // By principle component
      if (this.plotComponent) {
        const componentData =
          xlabel === "Diagnosis"
            ? this.newClassification["average_comp_ptA_diagnosis"]
            : this.newClassification["average_comp_ptA_CR"];

        plots.push({
          data: componentData.filter((_, index) => index % 3 == 0),
          type: "component",
          xlabel
        });
      }
      return plots;
    },
    thisPatientPlots() {
      const plots = [];
      const xlabel = this.timeFromDiagnosis ? "Diagnosis" : "CR";

      // This Patient Plot
      if (this.plotPatient) {
        const patientData =
          xlabel === "Diagnosis"
            ? this.thisPatient["ptA_diagnosis"]
            : this.thisPatient["ptA_CR"];

        plots.push({
          data: patientData.filter((_, index) => index % 3 == 0),
          type: "patient",
          xlabel
        });
      }
      return plots;
    },
    componentWidth() {
      return (
        (this.$refs.statePlots && this.$refs.statePlots.clientWidth) || 800
      );
    },
    curvesWidth() {
      return Math.floor((this.componentWidth * 2) / 3);
    }
  },
  mixins: [transitionMixin],
  methods: {
    toggleDiagnosisCR(timeFromDiagnosis) {
      this.timeFromDiagnosis = timeFromDiagnosis;
      this.updateBarPlots(1);
    },
    toggleNewELN(newProposal) {
      this.newProposal = newProposal;
      this.updateBarPlots(1);
    }
  }
};
</script>

<style lang="scss">
.transition-plots {
  .title {
    color: #00113d;
    margin-left: 12px;
    font-family: DM Sans;
  }
  text {
    font-family: DM Sans;
    fill: #00113d;
  }
  .state-plot-label {
    fill: #000;
  }
  .curves-axis {
    path {
      stroke: #ccc;
    }
    .tick line {
      stroke: #ccc;
    }
  }
  .ui.buttons {
    border: 1px solid #edeffa;
    border-radius: 2px;
    background: #f6f9ff;
    box-shadow: 0px 7px 20px rgba(130, 122, 132, 0.05);
    height: 40px;
    margin-left: 12px;
    margin-right: 12px;
    &.left {
      float: left;
    }
    &.right {
      float: right;
    }
    .active {
      color: white;
      background: #2e68bd;
      border-radius: 2px;
      height: 34px;
      margin: 2px;
      box-shadow: 0px 7px 20px rgba(130, 122, 132, 0.05);
    }
    .inactive {
      color: #7d859b;
      background: transparent;
    }
    .button:first-child {
      border-radius: 2px;
      margin: 2px;
    }
    .button:last-child {
      border-radius: 2px;
      margin: 2px;
    }
  }
  .ui.label.title-label {
    line-height: 15px;
    vertical-align: top;
  }

  .center-flex {
    display: flex;
    justify-content: center;
    margin: 30px 0px;
  }

  h2.title {
    margin-top: 0;
  }
  .survival-odds {
    color: #fff;
    font-weight: bolder;
  }

  img.placeholder {
    border-style: none;
    width: 50%;
    display: block;
    margin: auto;
    opacity: 0.2;
  }

  img.loading {
    position: absolute;
    top: 50%;
    display: block;
    left: 40%;
  }

  .state-plot-container {
    position: relative;
  }

  .wait-warning {
    margin: auto;
    text-align: center;
    position: absolute;
    top: 60%;
    left: calc(50% - 200px);
    font-weight: 400;
    font-size: 16px;
  }
}
</style>
